import { useEffect, useState } from 'react';
import {
  type ConsentRecord,
  type UserMe,
  changePassword,
  consentHistory,
  updateProfile,
} from '../services/api';
import { isLocale, useT, type Locale } from '../i18n';

interface AccountProps {
  user: UserMe;
  // Edicao de dados pessoais: chamado apos PATCH /me para o App manter o
  // estado do usuario sincronizado (ex.: preferred_language -> LocaleProvider).
  onUserUpdated?: (user: UserMe) => void;
}

const TYPE_LABEL: Record<string, string> = {
  terms: 'common.legal.terms',
  privacy: 'common.legal.privacy',
  cookies: 'common.legal.cookies',
};

// Mesmas opcoes do LanguageSwitcher — os 6 locales suportados pelo i18n.
const LOCALE_OPTIONS: { value: Locale; label: string }[] = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'zh-CN', label: '中文 (简体)' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
];

const INPUT_CLASS =
  'w-full rounded-md border border-brand-300 px-3 py-2 text-brand-900 focus:border-brand-500 focus:outline-none';
const LABEL_CLASS = 'mb-1 block text-sm font-medium text-brand-700';
const BUTTON_CLASS =
  'rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

function Account({ user, onUserUpdated }: AccountProps) {
  const t = useT();
  const [history, setHistory] = useState<ConsentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Meus dados ---------------------------------------------------------
  const [name, setName] = useState(user.name ?? '');
  const [language, setLanguage] = useState<Locale>(
    user.preferred_language && isLocale(user.preferred_language)
      ? user.preferred_language
      : 'pt-BR',
  );
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // --- Alterar senha -------------------------------------------------------
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let active = true;
    consentHistory()
      .then((rows) => {
        if (active) setHistory(rows);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : t('account.error.load'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault();
    setProfileMsg(null);
    if (!name.trim()) {
      setProfileMsg({ ok: false, text: t('account.profile.nameRequired') });
      return;
    }
    setProfileSaving(true);
    try {
      const updated = await updateProfile({
        name: name.trim(),
        preferred_language: language,
      });
      // A resposta do PATCH nao inclui role/impersonation — merge preserva.
      onUserUpdated?.({ ...user, ...updated });
      setProfileMsg({ ok: true, text: t('account.profile.saved') });
    } catch (err) {
      setProfileMsg({
        ok: false,
        text: err instanceof Error ? err.message : t('account.profile.error'),
      });
    } finally {
      setProfileSaving(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPwMsg(null);
    // Validacao client-side (mesmas regras do backend: min 8 chars).
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: t('account.password.mismatch') });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ ok: false, text: t('account.password.tooShort') });
      return;
    }
    setPwSaving(true);
    try {
      await changePassword(currentPw, newPw);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      setPwMsg({ ok: true, text: t('account.password.success') });
    } catch (err) {
      setPwMsg({
        ok: false,
        text: err instanceof Error ? err.message : t('account.password.error'),
      });
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* --- Meus dados --------------------------------------------------- */}
      <section className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-bold text-brand-900">{t('account.title')}</h1>
        <h2 className="mb-4 text-lg font-semibold text-brand-800">
          {t('account.profile.title')}
        </h2>

        <form onSubmit={handleProfileSubmit} className="max-w-md space-y-4">
          <div>
            <label htmlFor="profile-name" className={LABEL_CLASS}>
              {t('account.profile.name')}
            </label>
            <input
              id="profile-name"
              type="text"
              required
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('account.profile.namePlaceholder')}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="profile-language" className={LABEL_CLASS}>
              {t('account.profile.language')}
            </label>
            <select
              id="profile-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value as Locale)}
              className={INPUT_CLASS}
            >
              {LOCALE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="profile-email" className={LABEL_CLASS}>
              {t('account.profile.email')}
            </label>
            <input
              id="profile-email"
              type="email"
              value={user.email}
              readOnly
              disabled
              className="w-full cursor-not-allowed rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-brand-500"
            />
            <p className="mt-1 text-xs text-brand-500">{t('account.profile.emailNote')}</p>
          </div>

          <p className="text-sm text-brand-600">
            <span className="font-medium text-brand-800">{t('account.statusLabel')}</span>{' '}
            {user.is_active ? t('account.statusActive') : t('account.statusInactive')}
          </p>

          {profileMsg && (
            <p
              className={
                profileMsg.ok
                  ? 'rounded-md bg-green-50 px-3 py-2 text-sm text-green-700'
                  : 'rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'
              }
            >
              {profileMsg.text}
            </p>
          )}

          <button type="submit" disabled={profileSaving} className={BUTTON_CLASS}>
            {profileSaving ? t('account.profile.saving') : t('account.profile.save')}
          </button>
        </form>
      </section>

      {/* --- Alterar senha -------------------------------------------------- */}
      <section className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-brand-800">
          {t('account.password.title')}
        </h2>

        <form onSubmit={handlePasswordSubmit} className="max-w-md space-y-4">
          <div>
            <label htmlFor="pw-current" className={LABEL_CLASS}>
              {t('account.password.current')}
            </label>
            <input
              id="pw-current"
              type="password"
              required
              autoComplete="current-password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label htmlFor="pw-new" className={LABEL_CLASS}>
              {t('account.password.new')}
            </label>
            <input
              id="pw-new"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className={INPUT_CLASS}
            />
            <p className="mt-1 text-xs text-brand-500">{t('account.password.hint')}</p>
          </div>

          <div>
            <label htmlFor="pw-confirm" className={LABEL_CLASS}>
              {t('account.password.confirm')}
            </label>
            <input
              id="pw-confirm"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>

          {pwMsg && (
            <p
              className={
                pwMsg.ok
                  ? 'rounded-md bg-green-50 px-3 py-2 text-sm text-green-700'
                  : 'rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'
              }
            >
              {pwMsg.text}
            </p>
          )}

          <button type="submit" disabled={pwSaving} className={BUTTON_CLASS}>
            {pwSaving ? t('account.password.submitting') : t('account.password.submit')}
          </button>
        </form>
      </section>

      {/* --- Historico de consentimento ------------------------------------ */}
      <section className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-brand-800">{t('account.historyTitle')}</h2>
        <p className="mb-4 text-sm text-brand-600">
          {t('account.legalDocsIntro')}{' '}
          <a href="/terms" className="text-brand-700 underline" target="_blank" rel="noreferrer">{t('common.legal.termsShort')}</a>,{' '}
          <a href="/privacy" className="text-brand-700 underline" target="_blank" rel="noreferrer">{t('common.legal.privacyShort')}</a>,{' '}
          <a href="/cookies" className="text-brand-700 underline" target="_blank" rel="noreferrer">{t('common.legal.cookiesShort')}</a>
        </p>

        {loading && <p className="text-sm text-brand-600">{t('account.loading')}</p>}
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {!loading && !error && history.length === 0 && (
          <p className="text-sm text-brand-600">{t('account.empty')}</p>
        )}

        {!loading && !error && history.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-4">{t('account.col.document')}</th>
                  <th className="py-2 pr-4">{t('account.col.version')}</th>
                  <th className="py-2 pr-4">{t('account.col.accepted')}</th>
                  <th className="py-2 pr-4">{t('account.col.date')}</th>
                  <th className="py-2 pr-4">{t('account.col.ip')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-brand-800">{TYPE_LABEL[r.consent_type] ? t(TYPE_LABEL[r.consent_type]) : r.consent_type}</td>
                    <td className="py-2 pr-4 text-brand-600">{r.consent_version}</td>
                    <td className="py-2 pr-4">
                      {r.accepted ? (
                        <span className="text-green-700">{t('common.yes')}</span>
                      ) : (
                        <span className="text-red-700">{t('common.no')}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-brand-600">{formatDate(r.accepted_at)}</td>
                    <td className="py-2 pr-4 text-brand-600">{r.ip_address ?? t('common.dash')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default Account;
